/**
 * File: articles-meta.ts
 *
 * Pure helpers that turn what Skapedia returns (a list entry, and an article's
 * markdown) into the lightweight `ArticleMeta` the site works with. It is
 * imported in TWO places and therefore must not import anything at runtime (no
 * `marked`, no Vite virtual modules, no DOM; the one import below is a type):
 *   - at BUILD time by build/seo-plugins.ts, for sitemap.xml, feed.xml,
 *     llms.txt and the static article pages;
 *   - at RUNTIME by articles.ts.
 *
 * Where articles come from: they are published in Skapedia (src/code/skapedia.ts),
 * not kept in this repository. Skapedia names the files, makes the slug, the
 * date, the excerpt and the `news` flag, and serves the markdown.
 *
 * What the site still reads from the markdown itself:
 *   - the first level-1 heading (`# ...`) is the article title. The page shows
 *     it in its header, so it is left out of the rendered body.
 *   - the `# ` title is the page's only h1. A heading inside the body that should
 *     group the `##` sections after it is written `<h2 class="bq-h-major">`, and
 *     any other `# ` line in the body renders as an h2 (see ARTICLE_MARKED_OPTIONS).
 *   - optional one-line HTML comments override what search engines and link
 *     previews see, without touching the visible article. Each sits on its own
 *     line, anywhere in the file (by convention right under the title), and
 *     renders as nothing:
 *         <!-- seo-title: Shorter or more descriptive title for <title> -->
 *         <!-- seo-description: 120 to 160 characters, written for a person -->
 *         <!-- updated: 2026-09-19 -->
 *     `seo-title` replaces the article title in <title>, og:title and
 *     twitter:title only; the page heading stays the `# ` line. `updated` is the
 *     date of the last real content change (it feeds dateModified,
 *     article:modified_time and the sitemap lastmod). Unknown keys are ignored.
 *     The same convention is used by www.bunnyquery.com, so an article published
 *     for both sites keeps its overrides.
 *   These are not in a Skapedia list entry, so an article has them only once
 *   its markdown has been fetched (see `withBody`).
 */

import type { SkapediaArticle } from "skapedia";

export interface ArticleMeta {
    slug: string; // the name in the article's URL, /articles/<slug>
    file: string; // the published file name incl. extension
    url: string; // where Skapedia serves the markdown, what `getArticle()` takes
    title: string; // the article's title, or the slug as fallback
    date: string; // human-readable date for display ("July 8, 2026")
    dateSort: number; // sort key (epoch ms, or 0 when undated)
    excerpt: string; // plain-text lead paragraph for the list view
    banner: boolean; // true when Skapedia flags the article as news (`news`)
    seoTitle?: string; // `<!-- seo-title: ... -->`, the <title> text before the site-name suffix
    seoDescription?: string; // `<!-- seo-description: ... -->`, the meta description
    updated?: string; // `<!-- updated: YYYY-MM-DD -->`, last real content change
}

/** The override keys an article may carry, see the authoring convention above. */
export interface ArticleDirectives {
    seoTitle?: string;
    seoDescription?: string;
    updated?: string;
}

/**
 * Read the `<!-- key: value -->` override lines. One comment per line, so a
 * stray multi-line comment can never swallow the article. An `updated` value
 * that is not a real YYYY-MM-DD date is dropped, not guessed at.
 */
export function readDirectives(raw: string): ArticleDirectives {
    const out: ArticleDirectives = {};
    const re = /^[ \t]{0,3}<!--[ \t]*([a-z-]+)[ \t]*:[ \t]*([^\n]*?)[ \t]*-->[ \t]*$/gim;
    let m: RegExpExecArray | null;
    while ((m = re.exec(raw))) {
        const key = m[1].toLowerCase();
        const value = m[2].replace(/\s+/g, " ").trim();
        if (!value) continue;
        if (key === "seo-title") out.seoTitle = value;
        else if (key === "seo-description") out.seoDescription = value;
        else if (key === "updated") {
            const d = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            // Round-trip check: Date.UTC rolls 2026-02-30 over into March
            // instead of failing, so compare what comes back.
            const iso = d ? new Date(Date.UTC(+d[1], +d[2] - 1, +d[3])).toISOString().slice(0, 10) : "";
            if (iso === value) out.updated = value;
        }
    }
    return out;
}

/** One `<!-- key: value -->` override line (see readDirectives), whole line. */
const DIRECTIVE_LINE = /^[ \t]{0,3}<!--[ \t]*[a-z-]+[ \t]*:[^\n]*?-->[ \t]*$/gim;

/** The article body as markdown: the file minus its `# ` title line, which the
 *  page header renders on its own, and minus the override comments, which are
 *  metadata rather than content. */
export function articleBodyMarkdown(raw: string): string {
    return raw.replace(/^\s{0,3}#\s+.+?$/m, "").replace(DIRECTIVE_LINE, "");
}

/**
 * The `marked` options both article renderers use: the build-time prerender
 * (build/seo-plugins.ts) and the SPA (articles.ts). Kept here so the two can
 * never disagree about what an article's HTML is. This module still imports
 * nothing: the options are plain data plus one token visitor.
 *
 * The visitor turns any `# ` heading left in the body into an h2. The title is
 * already the page's h1, and a second h1 blurs what the page is about.
 */
export const ARTICLE_MARKED_OPTIONS = {
    gfm: true,
    breaks: false,
    async: false as const,
    walkTokens(token: { type: string; depth?: number }) {
        if (token.type === "heading" && token.depth === 1) token.depth = 2;
    },
};

/**
 * First real paragraph after the title, flattened to plain text and truncated.
 * Strips common inline markdown (emphasis, links, code, images) and skips any
 * leading raw-HTML block (articles often open with a hero `<img>`/`<svg>`, and
 * the override comments above start with `<` too) so the list preview reads as
 * prose.
 */
export function deriveExcerpt(raw: string): string {
    const withoutTitle = articleBodyMarkdown(raw);
    const lines = withoutTitle.split(/\r?\n/);
    const paragraph: string[] = [];
    for (const line of lines) {
        const t = line.trim();
        // Skip leading blanks / headings / block markers / raw HTML before the
        // first prose.
        if (!paragraph.length && (!t || /^([#>\-*+<]|\d+\.|```|\||!\[)/.test(t))) {
            continue;
        }
        if (!t) break; // blank line ends the paragraph
        paragraph.push(t);
    }
    let text = paragraph.join(" ");
    text = text
        .replace(/<[^>]*>/g, "") // inline html tags
        .replace(/!\[[^\]]*\]\([^)]*\)/g, "") // images
        .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1") // links -> label
        .replace(/`([^`]*)`/g, "$1") // inline code
        .replace(/[*_~]{1,3}([^*_~]+)[*_~]{1,3}/g, "$1") // emphasis
        .replace(/\s+/g, " ")
        .trim();
    if (text.length > 180) text = text.slice(0, 177).trimEnd() + "…";
    return text;
}

/**
 * A slug the site can use: it goes into the article's URL and, at build time,
 * into the path of its static page, so it is a plain name and never a path.
 */
export function isArticleSlug(slug: unknown): slug is string {
    return typeof slug === "string" && /^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(slug) && !slug.includes("..");
}

/**
 * A list entry's excerpt as plain text. An article that opens with markup (a
 * flag, a logo, a chart) can arrive with that markup as its excerpt, cut off
 * mid-tag. Tags are removed, and so is a tag the cut left unfinished; an
 * excerpt that was nothing but markup comes back empty, and the list then
 * shows the title alone. An article that opens with a quoted note arrives with
 * the quote's `>` marker in front, which is taken off.
 */
export function cleanExcerpt(excerpt: unknown): string {
    if (typeof excerpt !== "string") return "";
    return excerpt
        .replace(/<\/?[a-z!][^>]*>/gi, " ") // whole tags
        .replace(/<\/?[a-z!][^>]*$/i, "") // a tag cut off by the truncation
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^(?:>\s*)+/, ""); // the marker of a markdown quote
}

/**
 * The metadata record for one entry of a Skapedia list page. `null` for an
 * entry the site cannot show: no usable slug, or no address to fetch it from.
 */
export function fromSkapedia(item: SkapediaArticle): ArticleMeta | null {
    if (!item || !isArticleSlug(item.slug) || typeof item.url !== "string" || !item.url) return null;
    return {
        slug: item.slug,
        file: typeof item.file === "string" ? item.file : `${item.slug}.md`,
        url: item.url,
        title: (typeof item.title === "string" && item.title.trim()) || item.slug,
        date: typeof item.date === "string" ? item.date : "",
        dateSort: Number(item.dateSort) || 0,
        excerpt: cleanExcerpt(item.excerpt),
        banner: item.news === true,
    };
}

/**
 * The same record once the article's markdown is known: the override comments
 * are read from it, and an entry that had no usable excerpt gets the article's
 * lead paragraph. Spread last and only when set, so absent keys stay absent.
 */
export function withBody(article: ArticleMeta, raw: string): ArticleMeta {
    return {
        ...article,
        excerpt: article.excerpt || deriveExcerpt(raw),
        ...readDirectives(raw),
    };
}
